import { Grid, useEventCallback } from '@mui/material';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import Italian from '../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { IDLawer } from 'civil-status/constants/id-lawyer';

interface NegotiationDataProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  controllLawyerHusbandWife: (isLawyerHusband?: boolean, isLawyerWife?: boolean) => void;
}

const NegotiationDataComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  configuration,
  controllLawyerHusbandWife,
}: NegotiationDataProps): React.ReactElement => {
  const isNegoz1 = useMemo(() => {
    return configuration === ConfigurationType.NEGOZ1;
  }, [configuration]);

  const isNegoz2 = useMemo(() => {
    return configuration === ConfigurationType.NEGOZ2;
  }, [configuration]);

  useEffect(() => {
    controllLawyerHusbandWife(
      values?.receiptDocument === IDLawer.AvvocatoMarito ||
        values?.receiptDocument === IDLawer.AvvocatoEntrambi,
      values?.receiptDocument === IDLawer.AvvocatoMoglie ||
        values?.receiptDocument === IDLawer.AvvocatoEntrambi
    );
  }, [values?.receiptDocument]);

  const handleListTypeAgreement = useEventCallback((item?: ItemVM) => {
    onChange('typeAgreement', item?.id?.toString());
  });

  const handleListTypeMeasure = useEventCallback((item?: ItemVM) => {
    onChange('typeMeasure', item?.id?.toString());
  });

  const handleListReceiptDocument = useEventCallback((item?: ItemVM) => {
    onChange('receiptDocument', item?.id?.toString());
  });

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  return (
    <Grid container spacing={2} item>
      {isNegoz1 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Tipo accordo"
              idDominio="53"
              value={values?.typeAgreement || ''}
              onChange={handleListTypeAgreement}
              disabled={disabled}
              error={Boolean(errors?.typeAgreement) && touched?.typeAgreement}
              betweenFrom={1}
              betweenTo={5}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Tipo provvedimento"
              idDominio="99"
              value={values?.typeMeasure || ''}
              onChange={handleListTypeMeasure}
              disabled={disabled}
              error={Boolean(errors?.typeMeasure) && touched?.typeMeasure}
              betweenFrom={1}
              betweenTo={3}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Ricezione atto"
              idDominio="103"
              value={values?.receiptDocument || ''}
              onChange={handleListReceiptDocument}
              disabled={disabled}
              error={Boolean(errors?.receiptDocument) && touched?.receiptDocument}
              betweenFrom={1}
              betweenTo={3}
              required
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isNegoz2 ? (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data formazione documento *"
              aria-label="Data formazione documento"
              value={values?.documentDateFormation || null}
              onChange={(date) => {
                onChange('documentDateFormation', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="documentDateFormation"
                  fullWidth
                  helperText={errors?.documentDateFormation}
                  error={touched?.documentDateFormation && Boolean(errors?.documentDateFormation)}
                />
              )}
            />
          </Grid>
          <Italian
            disabledComponent={disabled}
            values={values?.italian}
            onChange={onChangeItalian}
            onChanges={onChangesItalian}
            errors={errors?.italian as FormikErrors<ItalianForm>}
            touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
            labelMunicipality="Comune ente"
            labelProvince="Provincia ente"
            required
            fromDate={values?.dateTranscription || null}
          />
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data deposito *"
              aria-label="Data deposito"
              value={values?.depositDate || null}
              onChange={(date) => {
                onChange('depositDate', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="depositDate"
                  fullWidth
                  helperText={errors?.depositDate}
                  error={touched?.depositDate && Boolean(errors?.depositDate)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <DatePicker
              label="Data comunicazione agli avvocati *"
              aria-label="Data comunicazione agli avvocati"
              value={values?.dateCommunication || null}
              onChange={(date) => {
                onChange('dateCommunication', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateCommunication"
                  fullWidth
                  helperText={errors?.dateCommunication}
                  error={touched?.dateCommunication && Boolean(errors?.dateCommunication)}
                />
              )}
            />
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

NegotiationDataComponent.displayName = 'Form Negotiation Data component';

const NegotiationData = memo(NegotiationDataComponent);

export default NegotiationData;
